import * as Sentry from "@sentry/react";

/**
 * Initialises the Sentry SDK against GlitchTip for the portal SPA.
 *
 * Call once at the very top of main.tsx before rendering.
 * No-ops when VITE_GLITCHTIP_DSN is empty — safe for local dev without
 * a GlitchTip instance running.
 *
 * Note: GlitchTip does not support sessions or performance tracing.
 * We capture errors only (tracesSampleRate: 0).
 *
 * No tunnel is used here — the portal is a pure client-side SPA with no
 * server routes to proxy through. Events go directly to the Caddy-fronted
 * GlitchTip endpoint (:8091). For production, update VITE_GLITCHTIP_DSN
 * to point at the public-facing GlitchTip URL.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_GLITCHTIP_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0,
    // Attach React component tree context to events.
    attachStacktrace: true,
  });
}

/**
 * Captures an unhandled error in Sentry/GlitchTip.
 * Signature is compatible with Sentry.ErrorBoundary onError prop.
 */
export function captureError(err: unknown): void {
  Sentry.captureException(err);
}
